import React, { useEffect, useRef, useState } from 'react';

import {
  ActionIcon,
  Badge,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconFilterOff, IconInfoSquareRounded, IconPrinter } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import HelpButton from 'components/HelpButton';
import KiloActionIcon from 'components/KiloActionIcon';
import KiloTooltip, { TOOLTIP_MESSAGES } from 'components/KiloTooltip';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { ExportToCsv, Options } from 'export-to-csv';
import useDiscountUsageReport from 'hooks/discount/useDiscountUsageReport';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { CellProps } from 'react-table';
import { DiscountList, DiscountUsageReportRedemption } from 'types';

interface DrawerDiscountUsageReportProps {
  opened: boolean;
  closeHandler: () => void;
  discount?: DiscountList;
}

const csvOptions: Options = {
  fieldSeparator: ',',
  quoteStrings: '"',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const buildCsvRow = (r: DiscountUsageReportRedemption): { [key: string]: string } => ({
  Athlete: r.athleteName,
  Date: dayjs(r.date).format('YYYY-MM-DD'),
  Product: r.productName,
});

const formatValueBadgeText = (
  discount: DiscountList,
  formatCurrency: (value: number) => string,
): string | null => {
  if (discount.valueType === 'PERCENTAGE' && discount.percentage != null) {
    const pct = Math.round(parseFloat(discount.percentage) * 100);
    return `${pct}% OFF`;
  }
  if (discount.valueType === 'FIXED_AMOUNT' && discount.amount != null) {
    return `${formatCurrency(discount.amount / 100)} OFF`;
  }
  return null;
};

const toApiDate = (d: Date | null): string | undefined => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

const DrawerDiscountUsageReport = ({
  opened,
  closeHandler,
  discount,
}: DrawerDiscountUsageReportProps): JSX.Element => {
  const { formatCurrency } = useLocalization();
  const { trackDiscountUsageReportViewed } = useStaffTracking();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const hasTrackedRef = useRef(false);
  const defaultsSetRef = useRef(false);

  const { data, isLoading, isFetching } = useDiscountUsageReport(opened ? discount?.discountId : undefined, {
    startDate: toApiDate(startDate),
    endDate: toApiDate(endDate),
  });

  const report = data?.data;
  const isBusy = isLoading || isFetching;

  useEffect(() => {
    if (!opened) {
      setStartDate(null);
      setEndDate(null);
      hasTrackedRef.current = false;
      defaultsSetRef.current = false;
    }
  }, [opened]);

  useEffect(() => {
    if (opened && report && discount && !hasTrackedRef.current) {
      trackDiscountUsageReportViewed(
        discount.discountId,
        discount.name,
        report.totalUses,
        report.totalAthletes,
        report.totalSaved,
      );
      hasTrackedRef.current = true;
    }
  }, [opened, report, discount, trackDiscountUsageReportViewed]);

  useEffect(() => {
    if (report && !defaultsSetRef.current) {
      setStartDate(dayjs(report.start).toDate());
      setEndDate(dayjs(report.end).toDate());
      defaultsSetRef.current = true;
    }
  }, [report]);

  const handleClearDates = (): void => {
    if (!report) return;
    setStartDate(dayjs(report.start).toDate());
    setEndDate(dayjs(report.end).toDate());
  };

  const handleExportCsv = (): void => {
    if (!report) return;
    const exporter = new ExportToCsv({
      ...csvOptions,
      filename: `discount-usage-${discount?.name ?? discount?.discountId}-${dayjs().format('YYYY-MM-DD')}`,
    });
    exporter.generateCsv(report.redemptions.map(buildCsvRow));
  };

  const valueBadgeText = discount ? formatValueBadgeText(discount, formatCurrency) : null;

  const italicHeader = (label: string): JSX.Element => (
    <Text italic color='dimmed' size='sm' weight={500}>
      {label}
    </Text>
  );

  const redemptionColumns = [
    {
      Header: italicHeader('Athlete'),
      id: 'athlete',
      Cell: ({ row }: CellProps<DiscountUsageReportRedemption>) => <Text>{row.original.athleteName}</Text>,
    },
    {
      Header: italicHeader('Date'),
      id: 'date',
      Cell: ({ row }: CellProps<DiscountUsageReportRedemption>) => (
        <Text>{dayjs(row.original.date).format('MMMM D, YYYY')}</Text>
      ),
    },
    {
      Header: italicHeader('Product'),
      id: 'product',
      Cell: ({ row }: CellProps<DiscountUsageReportRedemption>) => (
        <Text truncate maw={180}>
          {row.original.productName}
        </Text>
      ),
    },
  ];

  return (
    <Drawer opened={opened} onClose={closeHandler} title={<Title order={3}>Usage Report</Title>} size='lg'>
      <LoadingOverlay visible={isBusy} />

      {discount && (
        <Stack>
          <Group position='apart' noWrap>
            <Group spacing='xs' noWrap>
              <Text weight={600}>{discount.name}</Text>
              {valueBadgeText && (
                <Badge color='blue' variant='light' radius='sm'>
                  {valueBadgeText}
                </Badge>
              )}
              <Badge color={discount.isOneTimeUse ? 'green' : 'yellow'} variant='light' radius='sm'>
                {discount.isOneTimeUse ? 'ONE-TIME-USE' : 'UNLIMITED-USES'}
              </Badge>
            </Group>
            <Group spacing={4} noWrap>
              <Tooltip label='Reset to default range'>
                <ActionIcon variant='subtle' onClick={handleClearDates} disabled={!report}>
                  <IconFilterOff size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label='Export CSV'>
                <ActionIcon
                  variant='subtle'
                  onClick={handleExportCsv}
                  disabled={!report || report.redemptions.length === 0}
                >
                  <IconPrinter size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          <Text size='xs' color='dimmed'>
            Created {dayjs(discount.createdAt).format('MMMM D, YYYY')}
          </Text>

          <Group spacing='xs' noWrap>
            <DateInput
              value={startDate}
              onChange={setStartDate}
              valueFormat='MMMM D, YYYY'
              maxDate={endDate ?? undefined}
              sx={{ flex: 1 }}
            />
            <DateInput
              value={endDate}
              onChange={setEndDate}
              valueFormat='MMMM D, YYYY'
              minDate={startDate ?? undefined}
              maxDate={new Date()}
              sx={{ flex: 1 }}
            />

            <HelpButton width={260} size='lg' position='left' withinPortal>
              {TOOLTIP_MESSAGES.DISCOUNT_USAGE_REPORT}
            </HelpButton>
          </Group>

          <Group grow align='stretch'>
            <Paper bg='gray.1' p='md' sx={{ textAlign: 'center' }}>
              <Title order={2}>{report?.totalUses ?? 0}</Title>
              <Group spacing={4} position='center' noWrap>
                <Text size='sm' color='dimmed'>
                  Total Uses
                </Text>
                <KiloTooltip
                  wrapperDivClassVariantType='default'
                  label={TOOLTIP_MESSAGES.DISCOUNT_TOTAL_USES}
                  withArrow
                  position='top'
                >
                  <KiloActionIcon rootVariantType='default' variant='subtle' size='xs'>
                    <IconInfoSquareRounded size='0.95rem' />
                  </KiloActionIcon>
                </KiloTooltip>
              </Group>
            </Paper>
            <Paper bg='gray.1' p='md' sx={{ textAlign: 'center' }}>
              <Title order={2}>{report?.totalAthletes ?? 0}</Title>
              <Text size='sm' color='dimmed'>
                Athletes
              </Text>
            </Paper>
            <Paper bg='gray.1' p='md' sx={{ textAlign: 'center' }}>
              <Title order={2}>{formatCurrency((report?.totalSaved ?? 0) / 100)}</Title>
              <Text size='sm' color='dimmed'>
                Total Saved
              </Text>
            </Paper>
          </Group>

          <Divider />

          <Text weight={600}>Redemption History</Text>

          <Table
            tableData={report?.redemptions ?? []}
            columns={redemptionColumns}
            isSuccess={!!report}
            isLoading={isLoading}
            isError={false}
            margin={0}
            EmptyState={<Text color='dimmed'>No redemptions in this date range.</Text>}
          />
        </Stack>
      )}
    </Drawer>
  );
};

export default DrawerDiscountUsageReport;
